import { useState, useCallback, useEffect } from 'react';
import { useNotification } from '@/components/notifications';
import {
  getCollectionInfo,
  CollectionInfo,
} from '@/services/data-sources/get-collection-info';

interface UseCollectionInfoOptions {
  collectionId: string | undefined;
  isUnstructuredQdrantCollection: boolean;
}

interface UseCollectionInfoReturn {
  collectionInfo: CollectionInfo | null;
  collectionInfoLoading: boolean;
  formattedPointsCount: string;
  fetchCollectionInfo: () => Promise<void>;
}

export function useCollectionInfo({
  collectionId,
  isUnstructuredQdrantCollection,
}: UseCollectionInfoOptions): UseCollectionInfoReturn {
  const notification = useNotification();

  const [collectionInfo, setCollectionInfo] = useState<CollectionInfo | null>(
    null,
  );
  const [collectionInfoLoading, setCollectionInfoLoading] = useState(false);

  const fetchCollectionInfo = useCallback(async () => {
    if (!collectionId || !isUnstructuredQdrantCollection) {
      setCollectionInfo(null);
      return;
    }

    try {
      setCollectionInfoLoading(true);
      const result = await getCollectionInfo(collectionId);

      if ('detail' in result) {
        console.error('Failed to fetch collection info:', result.detail);
        notification.error('Failed to fetch collection metrics');
        setCollectionInfo(null);
      } else {
        setCollectionInfo(result);
      }
    } catch (error) {
      console.error('Failed to fetch collection info:', error);
      notification.error('Failed to fetch collection metrics');
      setCollectionInfo(null);
    } finally {
      setCollectionInfoLoading(false);
    }
  }, [collectionId, isUnstructuredQdrantCollection, notification]);

  useEffect(() => {
    fetchCollectionInfo();
  }, [fetchCollectionInfo]);

  const formattedPointsCount = (() => {
    if (collectionInfoLoading) return 'Loading...';
    if (typeof collectionInfo?.points_count === 'number') {
      return collectionInfo.points_count.toLocaleString();
    }
    return '—';
  })();

  return {
    collectionInfo,
    collectionInfoLoading,
    formattedPointsCount,
    fetchCollectionInfo,
  };
}
